import { ServiceProviderError } from '@takeshape/errors';
import { value } from '@takeshape/util';
import isObject from 'lodash/isObject.js';
import isString from 'lodash/isString.js';
import urlJoin from 'url-join';
import { DAY_IN_SECONDS } from "../constants.js";
import { updateAuthentication } from '../util/authentication.js';
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
function isValidAuthResponse(maybeResponse) {
    const response = maybeResponse;
    return isObject(response) && isObject(response.data) && isString(response.data.token);
}
function getStoreHash(apiPath) {
    return /bigcommerce.com\/stores\/([A-Za-z0-9]+)\//.exec(apiPath)?.[1];
}
const serviceProvider = {
    name: 'bigcommerce',
    title: 'BigCommerce',
    description: 'A powerful, headless ecommerce platform.',
    categories: ['ecommerce'],
    links: [
        { title: 'Homepage', url: 'https://www.bigcommerce.com/' },
        { title: 'Docs', url: '/docs/services/providers/bigcommerce' }
    ],
    serviceType: 'graphql',
    introspectionType: 'graphql',
    createId: value('bigcommerce'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9A-Za-z_-]+',
                default: 'big-commerce'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'Big Commerce'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'BigCommerce'
            },
            apiPath: {
                title: 'API Path',
                type: 'string',
                pattern: '^https://api.bigcommerce.com/stores/[A-Za-z0-9]+/v3/$'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    clientId: {
                        title: 'Client ID',
                        type: 'string',
                        '@sensitive': true
                    },
                    clientSecret: {
                        title: 'Client Secret',
                        type: 'string',
                        '@sensitive': true
                    },
                    accessToken: {
                        title: 'Access Token',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['clientId', 'clientSecret', 'accessToken']
            }
        },
        required: ['title', 'id', 'namespace', 'apiPath', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'apiPath', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'Human-readable identifier for use in the UI.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A pascal-case prefix that will be added to types in your BigCommerce API.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'Unique identifier for use in the API and Schema.'
            },
            apiPath: {
                widget: 'singleLineText',
                instructions: 'ex: https://api.bigcommerce.com/stores/5frbmvc2tl/v3/ from BigCommerce API Account.'
            },
            authentication: {
                properties: {
                    clientId: {
                        widget: 'sensitiveField'
                    },
                    clientSecret: {
                        widget: 'sensitiveField'
                    },
                    accessToken: {
                        widget: 'sensitiveField',
                        instructions: 'Access Token from BigCommerce API Account'
                    }
                },
                order: ['clientId', 'clientSecret', 'accessToken']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? 'BigCommerce',
            namespace: formData.namespace ?? 'BigCommerce',
            provider: 'bigcommerce',
            serviceType: 'graphql',
            authenticationType: 'custom'
        };
        if (isString(formData?.apiPath)) {
            const storeHash = getStoreHash(formData.apiPath);
            if (!storeHash) {
                throw new ServiceProviderError('BigCommerce - could not find a store hash in the api path');
            }
            config.options = {
                endpoint: `https://store-${storeHash}.mybigcommerce.com/graphql`,
                apiPath: formData.apiPath
            };
        }
        return updateAuthentication(config, formData, { type: 'custom' });
    },
    getAuthInfoCacheExpiration: value(DAY_IN_SECONDS * 30),
    getAuthInfoRequestConfig(serviceConfig) {
        const accessToken = serviceConfig.authentication?.accessToken;
        const apiPath = serviceConfig.options?.apiPath;
        if (!isString(accessToken) || !isString(apiPath)) {
            throw new ServiceProviderError('BigCommerce - invalid service config');
        }
        const nowInSeconds = Math.floor(Date.now() / 1000);
        return {
            url: urlJoin(apiPath, '/storefront/api-token'),
            method: 'POST',
            headers: {
                'X-Auth-Token': accessToken
            },
            json: {
                channel_id: 1, // The Storefront Channel is always 1
                expires_at: nowInSeconds + DAY_IN_SECONDS * 31
            }
        };
    },
    getAuthInfoResult(_, response) {
        if (!isValidAuthResponse(response)) {
            throw new ServiceProviderError('BigCommerce - invalid auth response');
        }
        return [[['headers', 'Authorization'], `Bearer ${response.data.token}`]];
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.apiPath']),
    getHealthCheck: getHealthCheck({ checkName: 'graphqlIntrospection' })
};
export default serviceProvider;
